import * as yup from 'yup'
import { AnyObject, Maybe } from 'yup/lib/types'

import {
  INVALID_PHONE_ERROR_MESSAGE,
  RESTRICTED_NAME_LAST_NAME_COMBINATIONS
} from '@js/common/constants'
import {
  CITY_VALIDATION_REGEX,
  EMAIL_VALIDATION_REGEXP,
  INAPPRIORIATE_WORD_REGEXP,
  LAST_NAME_VALIDATION_REGEXP,
  NAME_VALIDATION_REGEXP,
  POSTAL_CODE_LENGTH_BY_COUNTRY,
  POSTAL_CODE_REGEXP,
  STREET_VALIDATION_REGEX,
  validatePhoneByAPICall,
  validationMessage
} from '@js/common/validation'

export function firstName(this: yup.StringSchema, message: string) {
  return this.trim()
    .required(message ?? validationMessage.firstName.required)
    .min(2, message ?? validationMessage.firstName.tooShort)
    .test(
      'No inappropriate words',
      validationMessage.firstName.inappropriate,
      value => NAME_VALIDATION_REGEXP.test(value as string)
    )
}

export function lastName(this: yup.StringSchema, message: string) {
  return this.trim()
    .required(message ?? validationMessage.lastName.required)
    .min(2, message ?? validationMessage.lastName.tooShort)
    .test(
      'No inappropriate words',
      validationMessage.lastName.inappropriate,
      value => LAST_NAME_VALIDATION_REGEXP.test(value as string)
    )
    .test(
      'disallowed-pair',
      validationMessage.lastName.inappropriate,
      (value, context: yup.TestContext) => {
        const disallowedPairs = Object.entries(
          RESTRICTED_NAME_LAST_NAME_COMBINATIONS
        )
        const firstNameValue = context.parent.first_name
        if (
          disallowedPairs.some(
            ([disallowedName, disallowedLastName]) =>
              disallowedName === firstNameValue.toLowerCase() &&
              disallowedLastName === value?.toLocaleLowerCase()
          )
        ) {
          return false
        }
        return true
      }
    )
}

export function street(this: yup.StringSchema, message: string) {
  return this.trim()
    .required(message ?? validationMessage.street.required)
    .test(
      'No inappropriate words',
      validationMessage.street.inappropriate,
      value => STREET_VALIDATION_REGEX.test(value as string)
    )
}

export function streetNotRequired(this: yup.StringSchema) {
  return this.trim().test(
    'No inappropriate words',
    validationMessage.street.inappropriate,
    value => !value || STREET_VALIDATION_REGEX.test(value as string)
  )
}

export function streetWithoutInappropriateWords(
  this: yup.StringSchema,
  message: string
) {
  return this.trim()
    .required(message ?? validationMessage.street.requiredWithoutNumber)
    .test(
      'No inappropriate words',
      validationMessage.street.inappropriate,
      value => !INAPPRIORIATE_WORD_REGEXP.test(value as string)
    )
}

export function postalCode(this: yup.StringSchema, message: string) {
  return this.trim()
    .required(message ?? validationMessage.postalCode.required)
    .test('Postal Code', validationMessage.postalCode.postalCode, value =>
      POSTAL_CODE_REGEXP.test(value as string)
    )
    .test(
      'Matches chosen country',
      validationMessage.postalCode.countryNoMatch,
      (value, context: yup.TestContext) => {
        const country = context.parent.country

        const postalCodeLength = POSTAL_CODE_LENGTH_BY_COUNTRY[country]

        if (postalCodeLength) {
          return value?.length === postalCodeLength
        }

        return true
      }
    )
}
export function postalCodeNotRequired(this: yup.StringSchema) {
  return this.trim()
    .test(
      'Postal Code',
      validationMessage.postalCode.postalCode,
      value => !value || POSTAL_CODE_REGEXP.test(value as string)
    )
    .test(
      'Matches chosen country',
      validationMessage.postalCode.countryNoMatch,
      (value, context: yup.TestContext) => {
        const country = context.parent.country

        const postalCodeLength = POSTAL_CODE_LENGTH_BY_COUNTRY[country]

        if (postalCodeLength) {
          return value?.length === postalCodeLength
        }

        return true
      }
    )
}

export function city(this: yup.StringSchema, message: string) {
  return this.trim()
    .required(message ?? validationMessage.city.required)
    .test(
      'No inappropriate words',
      validationMessage.city.inappropriate,
      value => CITY_VALIDATION_REGEX.test(value as string)
    )
}

export function cityNotRequired(this: yup.StringSchema) {
  return this.trim().test(
    'No inappropriate words',
    validationMessage.city.inappropriate,
    value => !value || CITY_VALIDATION_REGEX.test(value as string)
  )
}

export function phone(this: yup.StringSchema, message: string) {
  return this.trim()
    .required(message || validationMessage.phone.required)
    .when(['phoneCountryPrefix'], (args, schema) => {
      const [countryCode] = args
      return schema.test(
        'Is valid phone',
        INVALID_PHONE_ERROR_MESSAGE,
        (number: string) => {
          return number ? validatePhoneByAPICall(countryCode, number) : true
        }
      )
    })
}

export function emailCustom(this: yup.StringSchema, message: string) {
  return this.trim()
    .required(message || validationMessage.email.required)
    .email(validationMessage.email.valid)
    .test('Is not fake email', validationMessage.email.valid, value =>
      EMAIL_VALIDATION_REGEXP.test(value as string)
    )
}

yup.addMethod(yup.string, 'firstName', firstName)
yup.addMethod(yup.string, 'lastName', lastName)
yup.addMethod(yup.string, 'street', street)
yup.addMethod(
  yup.string,
  'streetWithoutInappropriateWords',
  streetWithoutInappropriateWords
)
yup.addMethod(yup.string, 'postalCode', postalCode)
yup.addMethod(yup.string, 'city', city)
yup.addMethod(yup.string, 'phone', phone)
yup.addMethod(yup.string, 'streetNotRequired', streetNotRequired)
yup.addMethod(
  yup.string,
  'streetWithoutInappropriateWords',
  streetWithoutInappropriateWords
)
yup.addMethod(yup.string, 'postalCodeNotRequired', postalCodeNotRequired)
yup.addMethod(yup.string, 'cityNotRequired', cityNotRequired)
yup.addMethod(yup.string, 'emailCustom', emailCustom)

declare module 'yup' {
  interface StringSchema<
    TType extends Maybe<string> = string | undefined,
    TContext extends AnyObject = AnyObject,
    TOut extends TType = TType
  > extends yup.BaseSchema<TType, TContext, TOut> {
    firstName(): ReturnType<typeof firstName>
    lastName(): ReturnType<typeof lastName>
    street(): ReturnType<typeof street>
    streetWithoutInappropriateWords(): ReturnType<
      typeof streetWithoutInappropriateWords
    >
    postalCode(): ReturnType<typeof postalCode>
    city(): ReturnType<typeof city>
    streetNotRequired(): ReturnType<typeof streetNotRequired>
    postalCodeNotRequired(): ReturnType<typeof postalCodeNotRequired>
    cityNotRequired(): ReturnType<typeof cityNotRequired>
    phone(): ReturnType<typeof phone>
    emailCustom(): ReturnType<typeof emailCustom>
  }
}

export default yup
